import * as React from 'react'
import { ComponentProps, JSX } from 'react'
import { useTranslation } from 'react-i18next'

import { useFiltersApi } from '../../../hooks'

import { FilterProps } from '../../common/Filter/types'
import { SelectFilter } from '../../common/Filter/SelectFilter/SelectFilter'
import { type Option } from '../../common/Form/types'

import { useFilterLabels } from './useFilterLabels'
import { GetContractsRequestParams } from '@/hooks/useApi/useFiltersApi/types'
import { type Contract } from '@/hooks/useApi/types'

export type ContractFilterProps = {
    name: string
    onChange: (contract: string[]) => void
    value: string[]
    defaultValue: string[]
    disabled?: boolean
    valueLabel?: string
    params: Partial<GetContractsRequestParams>
} & Pick<FilterProps, 'containerRef'>

export const ContractFilter: React.FC<ContractFilterProps> = (props) => {
    const { disabled, value, defaultValue, name, params, onChange } = props

    const { useContractsQuery } = useFiltersApi()

    const query = useContractsQuery(params)

    const { data, isLoading, isError, isFetched, isFetching } = query
    const labels = useFilterLabels()
    const { t } = useTranslation('translations')

    const optionMap = React.useMemo<Map<string, Contract>>(() => {
        if (!data) return new Map<string, Contract>()
        return new Map(
            data.map((contract) => [contract.contractId.toString(), contract])
        )
    }, [data])

    const options = data?.map((contract) => {
        return {
            value: contract.contractId.toString(),
            label: contract.contractNumber,
        }
    })

    const renderRichLabel = React.useCallback(
        (
            option: Option,
            highlightMatchingText?: (input: string) => string | JSX.Element
        ) => {
            if (typeof option.value !== 'string') return option.label

            const rawOption = optionMap.get(option.value)

            if (rawOption) {
                return (
                    <div>
                        {highlightMatchingText?.(rawOption.contractNumber) ??
                            rawOption.contractNumber}
                    </div>
                )
            }

            return option.label
        },
        [optionMap]
    )

    const passedProps: ComponentProps<typeof SelectFilter> = {
        name,
        disabled: !!disabled,
        onChange: onChange,
        config: {
            value: value,
            options: options,
        },
        searchConfig: {
            name: `${name}-search`,
            placeholder: t('filters.contract.searchPlaceholder'),
        },
        value: value,
        defaultValue: defaultValue,
        renderRichLabel,
        label: t(`filters.contract.label`),
        ...labels,
        isLoading: isLoading || isFetching || !isFetched,
        isError,
        valueLabel: props.valueLabel,
        containerRef: props.containerRef,
    }

    return <SelectFilter {...passedProps} />
}
